import { component, equals } from '@/modules/common/components/ag-table/columns/utils';
import { h } from 'vue';
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  NestedFieldPaths,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { OrderIntent } from '@/connect/gen/consts/omsconsts_pb';
import { formatPrettyNumber } from '@/modules/common/components/pretty-number';
import Decimal from 'decimal.js';
import {
  BookOrder,
  QueryMarketplaceInstrument,
  QueryMarketplaceItem,
} from '@/connect/gen/modules/apiengine/services/venue/venue_pb';
import { getPriceAsString } from '@/utils/helpers/auction-numbers';
import MarketplaceExplorerActions from '@/modules/marketplace/components/MarketplaceExplorerActions.vue';
import { Side } from '@/connect/consts';
import { get as getProp } from 'lodash';
import { agreementAdapter } from '@/modules/common/components/ag-table/columns/common';
import { Data, RateType } from '@/connect';
import { filterSelect } from '@/modules/common/components/ag-table/columns/filter';
import { useCompaniesStore } from '@/store/companies-store';
import { Permission, useUserPermissions } from '@/composables/user-permissions';

type Direction = 'borrow' | 'lend';

function getDirectionFromSide(side: Side): Direction {
  // it is more convenient to convert the type because of the BE response
  return side === Side.BORROW ? 'borrow' : 'lend';
}

export function createColDefs(
  side: Side,
  oppositeSide: Side,
  rateType: RateType,
  asBroker: boolean
): [
  ColDef<QueryMarketplaceInstrument>,
  ColGroupDef<QueryMarketplaceItem>,
  ColGroupDef<QueryMarketplaceItem>,
] {
  const direction = getDirectionFromSide(side);
  const oppositeDirection = getDirectionFromSide(oppositeSide);

  const { userPermissions } = useUserPermissions();
  // no need to render actions for the viewer and broker (yet)
  const canHaveActions = !asBroker && userPermissions.value.has(Permission.OMS_ACTIONS);
  const mySideActions = !canHaveActions ? [] : [actions(direction)];
  const otherSideActions = !canHaveActions ? [] : [actions(oppositeDirection)];

  return [
    cusipRowGroup(),
    {
      headerName: direction,
      headerClass: `${direction}-header text-capitalize justify-center`,
      children: [
        ...mySideActions,
        orderIntent(direction),
        agreements(direction),
        quantity(direction),
        contractAmount(direction),
        rate(direction, rateType, true),
      ],
    },
    {
      headerName: oppositeDirection,
      headerClass: `${oppositeDirection}-header text-capitalize justify-center`,
      children: [
        rate(oppositeDirection, rateType),
        contractAmount(oppositeDirection),
        quantity(oppositeDirection),
        companyDisplayId(oppositeDirection),
        companyName(oppositeDirection),
        agreements(oppositeDirection),
        orderIntent(oppositeDirection),
        ...otherSideActions,
      ],
    },
  ];
}

export const defaultColDef: ColDef = {
  sortable: false,
  filter: false,
  suppressHeaderMenuButton: false,
};

export function cusipRowGroup(): ColDef<QueryMarketplaceInstrument> {
  return {
    field: 'instrument.cusip',
    colId: 'cusip',
    rowGroup: true,
    hide: true,
    valueFormatter: (params: ValueFormatterParams<QueryMarketplaceInstrument>) => {
      const data = params.data;
      if (!data || !data.instrument) return '';
      const prefix = data.instrument.ticker ? `${data.instrument.ticker} | ` : '';
      return `${prefix}${data.instrument.cusip} | ${data.instrument.name} | $${new Decimal(data.instrument.lastClosePrice ?? 0).toFixed(2)} (${data.orderCount})`;
    },
  };
}

const actionsAdapter = component<{
  order: BookOrder;
  direction: Direction;
}>((props) => () => h(MarketplaceExplorerActions, { props }));

function actions(direction: Direction): ColDef<QueryMarketplaceItem> {
  return {
    colId: `${direction}Actions`,
    cellRendererSelector: (params) => {
      if (!params.data) return undefined;
      const order = params.data[`${direction}Order`];
      // no need to render actions if order is not present (pair with only 1 order)
      if (!order) return undefined;
      return actionsAdapter({ order, direction });
    },
    suppressColumnsToolPanel: true,
  };
}

function getField(
  direction: Direction,
  field: keyof Data<BookOrder>
): NestedFieldPaths<QueryMarketplaceItem, unknown, []> {
  return `${direction === 'borrow' ? 'borrowOrder' : 'lendOrder'}.${field}` as NestedFieldPaths<
    QueryMarketplaceItem,
    unknown,
    []
  >;
}

function getIoiOrderClassRule(direction: Direction) {
  return (params: CellClassParams<QueryMarketplaceItem>) =>
    getProp(params.data, `${direction}Order.orderIntent`) === OrderIntent.IOI;
}

export function orderIntent(direction: Direction): ColDef<QueryMarketplaceItem> {
  const field = getField(direction, 'orderIntent');
  return {
    field,
    colId: `${direction}OrderIntent`,
    headerName: 'Intent',
    valueFormatter: (params: ValueFormatterParams<QueryMarketplaceItem, OrderIntent>) => {
      switch (params.value) {
        case OrderIntent.IOI:
          return 'IOI';
        case OrderIntent.FIRM:
          return 'Firm';
        default:
          return '';
      }
    },
    cellClassRules: {
      'ioi-order': getIoiOrderClassRule(direction),
    },
    cellClass: 'justify-start',
    ...filterSelect([
      { text: 'All', value: null },
      { text: 'Firm', value: OrderIntent.FIRM },
      { text: 'IOI', value: OrderIntent.IOI },
    ]),
  };
}

export function agreements(direction: Direction): ColDef<QueryMarketplaceItem> {
  const field = getField(direction, 'agreementIds');
  return {
    field,
    colId: `${direction}Agreements`,
    headerName: 'Agreements',
    enableCellChangeFlash: true,
    cellRendererSelector: (params) =>
      agreementAdapter({ agreementIds: params.value, emptyValue: '' }),
    cellClassRules: {
      'ioi-order': getIoiOrderClassRule(direction),
    },
    cellClass: 'justify-start',
    equals: equals.array,
  };
}

export function companyDisplayId(direction: Direction): ColDef<QueryMarketplaceItem> {
  const companiesStore = useCompaniesStore();
  const field = getField(direction, 'companyId');
  return {
    field,
    colId: `${direction}CompanyID`,
    headerName: 'Company ID',
    enableCellChangeFlash: true,
    valueFormatter: (params) => {
      if (!params.value) return '';
      const company = companiesStore.getCompany(params.value);
      if (!company) return '';
      return company?.displayId ?? '';
    },
    cellClassRules: {
      'ioi-order': getIoiOrderClassRule(direction),
    },
    minWidth: 55,
    initialHide: true,
  };
}

export function companyName(direction: Direction): ColDef<QueryMarketplaceItem> {
  const companiesStore = useCompaniesStore();
  const field = getField(direction, 'companyId');
  return {
    field,
    colId: `${direction}CompanyName`,
    headerName: 'Company Name',
    enableCellChangeFlash: true,
    valueFormatter: (params) => {
      if (!params.value) return '';
      const company = companiesStore.getCompany(params.value);
      return company?.companyName ?? '';
    },
    cellClassRules: {
      'ioi-order': getIoiOrderClassRule(direction),
    },
    cellClass: 'justify-start',
    minWidth: 96,
  };
}

export function quantity(direction: Direction): ColDef<QueryMarketplaceItem> {
  const field = getField(direction, 'quantity');
  return {
    field,
    colId: `${direction}Quantity`,
    headerName: 'Quantity',
    enableCellChangeFlash: true,
    valueFormatter: (params: ValueFormatterParams<QueryMarketplaceItem>) =>
      params.value ? formatPrettyNumber(params.value) : '',
    cellClassRules: {
      'ioi-order': getIoiOrderClassRule(direction),
    },
    headerClass: 'header-right',
    cellClass: 'justify-end',
    floatingFilter: true,
    filter: 'agNumberColumnFilter',
    filterParams: {
      filterOptions: ['equals', 'lessThanOrEqual', 'greaterThanOrEqual'],
      debounceMs: 200,
      maxNumConditions: 1,
      allowedCharPattern: '\\d\\,',
      numberParser: (text: string | null) => {
        if (!text) return null;
        return Number(text.replace(/,/g, ''));
      },
    },
  };
}

export function contractAmount(direction: Direction): ColDef<QueryMarketplaceItem> {
  const field = getField(direction, 'marketValue');
  return {
    field,
    colId: `${direction}Value`,
    headerName: 'Market Value',
    enableCellChangeFlash: true,
    valueFormatter: (params: ValueFormatterParams<QueryMarketplaceItem>) => {
      return params.value ? `$${getPriceAsString(params.value, 0)}` : '';
    },
    headerClass: 'header-right',
    cellClass: 'justify-end',
    cellClassRules: {
      'ioi-order': getIoiOrderClassRule(direction),
    },
  };
}

export function rate(
  direction: Direction,
  rateType: RateType,
  isHighlighted = false
): ColDef<QueryMarketplaceItem> {
  const field = getField(direction, rateType === RateType.FEE ? 'fee' : 'rebate');
  return {
    field,
    colId: `${direction}Rate`,
    headerName: rateType === RateType.FEE ? 'Fee Rate' : 'Rebate Rate',
    enableCellChangeFlash: true,
    valueFormatter: (params: ValueFormatterParams<QueryMarketplaceItem>) => {
      const hasOrder = getProp(params.data, `${direction}Order`);
      return params.value ? new Decimal(params.value).toFixed(3) : hasOrder ? '-' : '';
    },
    headerClass: 'header-right',
    cellClass: 'justify-end',
    cellClassRules: {
      'highlighted-border-right': () => isHighlighted,
      'ioi-order': getIoiOrderClassRule(direction),
    },
    floatingFilter: true,
    filter: 'agNumberColumnFilter',
    filterParams: {
      filterOptions: ['equals', 'lessThanOrEqual', 'greaterThanOrEqual'],
      debounceMs: 200,
      maxNumConditions: 1,
      allowedCharPattern: '\\d\\.\\-',
      numberParser: (text: string | null) => {
        if (!text) return null;
        // never convert the string to a number,
        // pass the string as-is to the filter
        return text;
      },
    },
  };
}
